import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import type { PlanType } from 'webapp/enums/enums';
import { BillingAddonType, BillingFeature } from 'webapp/enums/enums';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonRequestTrialButtonComponent from 'webapp/components/common/request-trial-button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanButtonComponent from 'webapp/components/common/upgrade-plan-button';
import CommonUpgradePlanLinkComponent from 'webapp/components/common/upgrade-plan-link';
import SubmitButtonComponent from 'webapp/components/submit-button';

import BillingPlan from 'webapp/models-custom/billing-plan';
import type SourceType from 'webapp/models-custom/source-type';

export interface Args {
	feature: BillingFeature;
	trackingOrigin?: string; // in case we want to track this instead of feature (e.g. ai summary block in designer)
	sourceType?: SourceType;
	isInline?: boolean; // Used only for BillingFeature.GENERATIVE_AI
	isTextOnly?: boolean; // from block (BillingFeature.VISUALIZATIONS_ADVANCED, GENERATIVE_AI)
	buttonOnly?: boolean; // render only the controls block
	buttonTheme?: string; // SubmitButton theme passed to the primary CTAs
	buttonSize?: string; // SubmitButton size (e.g. "small") passed to the primary CTAs
	close?: () => void;
}

export default class BillingForbiddenFeatureComponent<T extends Args> extends Component<T> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;

	@tracked isClosing = false;
	@tracked isFeatureEnableInProgress = false;

	get featureValue() {
		return this.billingService.getFeatureValue(this.args.feature);
	}

	get isCustomMetricLimitReached() {
		const customMetricLimit = this.billingService.activePlan?.features.customMetricsMax,
			queriesCount = this.mmCustomQueriesService.savedRecords.length,
			isCustomQueryLimitSetAndUserHasSome =
				typeof customMetricLimit === 'number' && queriesCount !== 0;

		return isCustomQueryLimitSetAndUserHasSome ? queriesCount >= customMetricLimit : false;
	}

	get featureName(): string {
		const feature = this.args.feature;
		return this.billingService.getFeatureName(feature) || feature;
	}

	get isAgencyAdminInClientSpace() {
		const { isAdmin, isAgencyUserInClientSpace } = this.currentUserService;
		return isAdmin && isAgencyUserInClientSpace;
	}

	get text() {
		let text = '';

		const { sourceType } = this.args,
			isAdmin = this.currentUserService.isAdmin,
			isClient = this.accountService.isClient,
			currentLimit = this.featureValue as number,
			activePlan = this.billingService.activePlan,
			featureName = this.featureName;

		let isLastSentenceLowercase = false;

		if (sourceType) {
			text = `${sourceType.name} requires the use of ${this.featureName}, which is available on ${this.targetPlanText}. `;
		} else if (this.args.feature === BillingFeature.SOURCES && this.billingService.isCanceled) {
			text = `<strong>You are limited to ${this.billingService.sourcesMaxCanConnect} ${featureName}.</strong><br />`;
			text += `To connect more ${featureName}, please `;
			if (!isClient && isAdmin) {
				text += 'resubscribe.';
			}
		} else if (
			this.args.feature === BillingFeature.SOURCES &&
			this.billingService.isExpiredTrial
		) {
			text = `<strong>Your trial has expired.</strong><br />`;
			text += `To connect more ${featureName}, please `;
			if (!isClient && isAdmin) {
				text += 'subscribe.';
			}
		} else if (
			this.billingService.features.isCountable(this.args.feature) &&
			currentLimit > 0
		) {
			if (this.isBoards) {
				text = "<strong>You're at your current dashboard limit.</strong><br />";
				text += 'To create more dashboards, you need to upgrade your plan.';
				text += '<br>';
			} else if (this.isCustomMetrics) {
				if (this.isCustomMetricLimitReached) {
					text =
						'<strong>You have reached the built metric limit in your account.</strong><br />';
					text += 'Please upgrade your plan to create more built metrics.';
				} else {
					text = `<strong>You have reached the built metric limit in your account (${currentLimit}).</strong><br />`;
					text +=
						'Please review the built metrics in your account and remove any unused built metrics. ';
					text +=
						'<a href="https://help.databox.com/how-do-i-manage-my-custom-metrics" target="_blank">How to increase my built metric limit?</a>';
				}
			} else {
				let action = 'create';

				let planText;
				if (activePlan) {
					planText = `The ${activePlan.name} plan`;
				} else {
					planText = `Your plan`;
				}

				text = `<strong>${planText} is limited to ${currentLimit} ${currentLimit === 1 ? featureName.slice(0, -1) : featureName}.</strong><br />`;

				if (this.args.feature === BillingFeature.SOURCES) {
					action = 'connect';
				} else if (this.args.feature === BillingFeature.USERS) {
					action = 'invite';
				}

				text += `To ${action} more ${featureName}, please `;
				if (!isClient && isAdmin) {
					if (this.billingService.isCanceled) {
						text += 'resubscribe.';
					} else {
						text += 'upgrade your plan.';
					}
				}
			}
		} else if (this.args.feature === BillingFeature.DATA_REFRESH_INTERVAL) {
			let planText;
			if (activePlan) {
				planText = `the ${activePlan.name} plan`;
			} else {
				planText = `your plan`;
			}

			text = `<strong>With ${planText} data is synced only once a day.</strong><br />`;
			text += 'To sync every hour, please ';
			if (!isClient && isAdmin) {
				text += 'upgrade your plan.';
			}
		} else if (this.isPrioritySync) {
			text = '<strong>You’ve reached the daily limit of manual data updates.</strong><br />';
			text += 'To enable faster sync, ';
			isLastSentenceLowercase = true;
		} else if (this.isCreateDataboardsAI) {
			text = 'You’ve reached your daily limit for AI-created items.<br />';
		} else {
			text = `This requested feature is available on ${this.targetPlanText}.<br />`;
			if (!isClient && isAdmin) {
				text += 'You can upgrade your plan at any time.';
			}
		}

		if (!isAdmin || (isClient && !this.isAgencyAdminInClientSpace)) {
			let finalText = 'Please ';

			if (isLastSentenceLowercase) {
				finalText = finalText.toLowerCase();
			}

			text += ` <strong>${finalText} contact your account owner</strong>`;

			if (!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins) {
				text += ` or any <strong>administrator</strong>`;
			}

			text += ` to upgrade your plan.`;
		}

		return text;
	}

	get mainFeature(): BillingFeature {
		return this.args.feature === BillingFeature.AI_DATABOARDS
			? BillingFeature.GENERATIVE_AI
			: this.args.feature;
	}

	get targetPlan(): BillingPlan | null {
		return this.billingService.getNextPlanForFeatureWithSameBillingCycle(this.mainFeature);
	}

	get isPlanForTrialOverriden(): boolean {
		return this.billingService.checkIfPlanForTrialIsOverriden(this.targetPlanType);
	}

	get targetPlanType(): PlanType | '' {
		return this.targetPlan?.planType || '';
	}

	get targetPlanCapitalized(): string {
		return capitalize(this.targetPlanType);
	}

	get targetPlanText(): string {
		if (this.isPlanForTrialOverriden) {
			return 'paid plans';
		}

		const targetPlanCapitalized = this.targetPlanCapitalized,
			endingSentence = 'higher plans';
		if (targetPlanCapitalized) {
			return `${targetPlanCapitalized} ${this.isTargetPlanNameHighest ? ' plan' : ` and ${endingSentence}`}`;
		} else {
			return endingSentence;
		}
	}

	get isTargetPlanNameHighest(): boolean {
		const planTypes = BillingPlan.planTypes;
		return planTypes[planTypes.length - 1] === this.targetPlanType;
	}

	get topIcon() {
		let icon: IconName = 'ArrowUpCircleFill';
		if (this.isCustomMetrics && !this.isCustomMetricLimitReached) {
			icon = 'DataSearch';
		}
		return icon;
	}

	get canAct() {
		return this.billingService.canActOnLimitations;
	}

	get showPlanComparison() {
		return this.canAct && !this.isCustomMetrics && !this.isCreateDataboardsAI;
	}

	get cancelText() {
		const canActText =
			this.args.feature === BillingFeature.GENERATIVE_AI
				? "I'll stick with the current plan"
				: "I'm not ready yet";
		return this.canAct ? canActText : 'OK, got it!';
	}

	get payingTrialPromotions() {
		const targetPlan = this.targetPlan,
			payingTrialPromotions = targetPlan
				? this.flowPromotionsService.getHigherPlanAutomatedTrialOptionPromotionsForPlanType(
						targetPlan.planType,
					)
				: [];

		return payingTrialPromotions;
	}

	get isRequestTrialButtonShown() {
		const featuresThatCanBeTriedOut = [
			BillingFeature.QB,
			BillingFeature.USERS,
			BillingFeature.SCRIPTS,
			BillingFeature.CUSTOM_METRICS,
			BillingFeature.SOURCES,
			BillingFeature.PREMIUM_SOURCES,
			BillingFeature.ADVANCED_REPORTS,
			BillingFeature.API_METRIC_BUILDER,
			BillingFeature.VISUALIZATIONS_ADVANCED,
			BillingFeature.GENERATIVE_AI,
		];

		let showLink = false;
		if (
			featuresThatCanBeTriedOut.includes(this.args.feature) &&
			(this.billingService.canRequestTrial ||
				this.billingService.isActiveTrial ||
				!!this.payingTrialPromotions.length)
		) {
			showLink = true;
		}
		return showLink;
	}

	get showCancelLink() {
		return (
			this.hasClose &&
			![
				BillingFeature.QB,
				BillingFeature.SCRIPTS,
				BillingFeature.CUSTOM_METRICS,
				BillingFeature.API_METRIC_BUILDER,
				BillingFeature.ACTIVITY_LOG,
			].includes(this.args.feature)
		);
	}

	get showUpgradePlanLink() {
		return this.isActivityLog && !this.isRequestTrialButtonShown;
	}

	get agencyAdmin() {
		if (this.accountService.isClient) {
			return this.mmUsersService.agencyAdminRecordForClientSpace;
		} else {
			return this.accountService.accountOwner;
		}
	}

	get generativeAiInlineImgSrc(): string {
		return `/images/generative-ai/blurred${this.navigationService.theme.isDark ? '-dark' : ''}.png`;
	}

	get isBoards() {
		return this.args.feature === BillingFeature.BOARDS;
	}

	get isCustomMetrics() {
		return this.args.feature === BillingFeature.CUSTOM_METRICS;
	}

	get isQb() {
		return this.args.feature === BillingFeature.QB;
	}

	get isScripts() {
		return this.args.feature === BillingFeature.SCRIPTS;
	}

	get isReportsAdvanced() {
		return this.args.feature === BillingFeature.ADVANCED_REPORTS;
	}

	get isGenerativeAI() {
		return this.args.feature === BillingFeature.GENERATIVE_AI;
	}

	get isApiMetricBuilder() {
		return this.args.feature === BillingFeature.API_METRIC_BUILDER;
	}

	get isVisualizationsAdvanced() {
		return this.args.feature === BillingFeature.VISUALIZATIONS_ADVANCED;
	}

	get isActivityLog() {
		return this.args.feature === BillingFeature.ACTIVITY_LOG;
	}

	get isOKRs() {
		return this.args.feature === BillingFeature.OKR;
	}

	get isPrioritySync() {
		return this.args.feature === BillingFeature.PRIORITY_SYNC;
	}

	get isCreateDataboardsAI() {
		return this.args.feature === BillingFeature.AI_DATABOARDS;
	}

	get hasClose() {
		return !!this.args.close;
	}

	get upgradeButtonText() {
		if (this.isPrioritySync) {
			return 'Upgrade';
		}

		return;
	}

	get showRequestUpgradeForAgencyNonAdmins() {
		if (this.navigationService.isDatawallApp) {
			return this.navigationService.isDatawallAndHasAccessToBoard;
		} else {
			return !this.currentUserService.isAdmin && !this.accountService.isClient;
		}
	}

	@action
	close() {
		const close = this.args.close;
		if (close) {
			close();
		}
	}

	@action
	contactSupport() {
		this.currentUserService.chatWithSupport();
	}

	@action
	confirm() {
		if (this.isReportsAdvanced) {
			this.billingService.transitionToPlansAndShowReportsGrowthBubble();
		} else {
			this.navigationService.transitionToRoute('settings.billing.plans');
		}
		this.close();
	}

	@action
	cleanupCustomMetrics() {
		void this.mmMetricsService.openUnusedMetricCleanupPopup();
	}

	@action
	enableFeature() {
		this.isFeatureEnableInProgress = true;
		void this.mmActivityLogsService.enableAddon().finally(() => {
			this.isFeatureEnableInProgress = false;
		});
	}

	@action
	openActivityLogPurchasePopup() {
		this.billingService.openActivityLogAddonPurchasePopup();
	}

	@action
	openOKRsPurchasePopup() {
		const addon = this.billingService.getAddonInfo(BillingAddonType.OKR);

		if (addon) {
			this.billingService.openAddonPurchasePopup(addon);
		}
	}

	@action
	requestPlanUpgrade() {
		this.billingService.openRequestUpgradePopup(this.args.feature);
	}

	@action
	redirectToAgencySpace(): void {
		this.mmSpacesService.redirectToAgencySpace();
	}

	@action
	setupFeaturePromoSlider(node: HTMLElement) {
		const nodelEl = $(node);
		nodelEl.slick({
			speed: 500,
			autoplay: true,
			autoplaySpeed: 3000,
			slidesToShow: 1,
			slidesToScroll: 1,
			infinite: true,
			arrows: false,
			dots: true,
			appendDots: nodelEl.next('.dots'),
			useTransform: true,
		});
	}

	@action
	teardownFeaturePromoSlider(node: HTMLElement) {
		if (node?.classList.contains('slick-initialized')) {
			$(node).slick('unslick');
		}
	}
	<template>
		{{#unless @buttonOnly}}
			{{#if this.isReportsAdvanced}}
				<div class="feature-image-slider">
					<ul
						{{didInsert this.setupFeaturePromoSlider}}
						{{willDestroy this.teardownFeaturePromoSlider}}
					>
						<li>
							<img
								src="/images/reports/slide-promo-1-{{this.navigationService.theme.id}}.png"
								alt="Text-type slide"
							/>
						</li>
						<li>
							<img
								src="/images/reports/slide-promo-2-{{this.navigationService.theme.id}}.png"
								alt="Img-type slide"
							/>
						</li>
						<li>
							<img
								src="/images/reports/slide-promo-3-{{this.navigationService.theme.id}}.png"
								alt="Side by side-type slide"
							/>
						</li>
						<li>
							<img
								src="/images/reports/slide-promo-4-{{this.navigationService.theme.id}}.png"
								alt="Data story-type slide"
							/>
						</li>
					</ul>
					<div class="dots"></div>
				</div>

				<span class="title">Upgrade to unlock this feature</span>

				<p>
					<strong>Better insights = better reports.</strong>
					Bring more meaning to every number by unlocking the full functionality of
					{{#unless this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins}}
						Databox
					{{/unless}}
					Reports.
					{{#unless this.accountService.hideHelpArticles}}
						<a
							href="https://help.databox.com/create-a-slide-presentation"
							target="_blank"
						>
							Learn more
						</a>
					{{/unless}}
				</p>

				{{#unless this.canAct}}
					<p>{{{this.text}}}</p>
				{{/unless}}
			{{else if this.isVisualizationsAdvanced}}
				<div class="header blue-on-transparent">
					<CommonUiIconComponent @name={{this.topIcon}} @size="xl" @class="top-icon" />
				</div>
				<span class="title">Upgrade to unlock this visualization</span>

				<p>
					<strong>Data story.</strong>
					Bring more meaning to every number by unlocking the full Data story
					functionality.
					{{!-- {{#unless this.accountService.hideHelpArticles}}
                <a href="https://help.databox.com" target="_blank">Learn more</a>
            {{/unless}} --}}
				</p>
				{{#unless this.canAct}}
					<p>{{{this.text}}}</p>
				{{/unless}}
			{{else if this.isGenerativeAI}}
				{{#if @isInline}}
					<img src={{this.generativeAiInlineImgSrc}} alt="performance summary example" />
				{{else}}
					{{#unless @isTextOnly}}
						<div class="feature-image-slider">
							<ul
								{{didInsert this.setupFeaturePromoSlider}}
								{{willDestroy this.teardownFeaturePromoSlider}}
							>
								<li>
									<img
										src="/images/generative-ai/slide-promo-1-{{this.navigationService.theme.id}}.svg"
										alt="Text-type slide"
									/>
								</li>
								<li>
									<img
										src="/images/generative-ai/slide-promo-2-{{this.navigationService.theme.id}}.svg"
										alt="Img-type slide"
									/>
								</li>
							</ul>
							<div class="dots"></div>
						</div>
					{{/unless}}

					{{#unless this.canAct}}
						<p>{{{this.text}}}</p>
					{{/unless}}
				{{/if}}
			{{else if this.isActivityLog}}
				<div class="feature-image-slider">
					<ul>
						<li>
							<img
								src="/images/activity-log/slide-promo-1-{{this.navigationService.theme.id}}.png"
								alt="Activity log"
								height="200"
							/>
						</li>
					</ul>
					<div class="dots"></div>
				</div>

				<span class="title">
					Enable Advanced security management add-on<br />
					and get access to Activity log
				</span>

				<p>
					Unlock advanced security features and gain complete visibility into<br />
					the changes made to your account settings and users.
					<a href="https://help.databox.com/review-active-user-sessions" target="_blank">
						Learn more
					</a>
				</p>
			{{else if this.isPrioritySync}}
				<div class="header blue-on-transparent">
					<CommonUiIconComponent @name="Refresh" @size="md" @class="top-icon" />
				</div>

				<span class="title">Enable faster sync frequency</span>

				<p>
					{{{this.text}}}

					{{#if this.showPlanComparison}}
						<a role="button" {{on "click" this.confirm}}>See plan comparison</a>
					{{/if}}
				</p>
			{{else}}
				<div class="header blue-on-transparent">
					<CommonUiIconComponent @name={{this.topIcon}} @size="xl" @class="top-icon" />
				</div>
				<span class="title">Upgrade plan</span>

				<p>
					{{{this.text}}}

					{{#if this.showPlanComparison}}
						<a role="button" {{on "click" this.confirm}}>See plan comparison</a>
					{{/if}}
				</p>

			{{/if}}
		{{/unless}}

		<div class="controls">
			{{#if this.canAct}}
				{{#if this.isCustomMetrics}}
					{{#if this.isCustomMetricLimitReached}}
						<a
							role="button"
							class="button theme-grey-outlined"
							{{on "click" this.cleanupCustomMetrics}}
						>
							Clean up custom metrics
						</a>
					{{/if}}
					{{#if this.aiChatService.hasSupportChat}}
						<a
							role="button"
							class="button theme-grey-outlined"
							{{on "click" this.contactSupport}}
						>
							Contact support
						</a>
					{{/if}}
				{{else if this.showUpgradePlanLink}}
					<CommonUpgradePlanLinkComponent
						@feature={{@feature}}
						@trackingOrigin={{@trackingOrigin}}
					/>
				{{/if}}

				{{#unless this.accountService.hideHelpArticles}}
					{{#if this.isQb}}
						<a
							class="cancel"
							href="https://databox.com/resources/help/query-builder"
							target="_blank"
						>
							Read more about the Metric builder
						</a>
					{{/if}}

					{{#if this.isScripts}}
						<a
							class="cancel"
							href="https://help.databox.com/overview-calculated-metrics"
							target="_blank"
						>
							Read more about the Data calculations
						</a>
					{{/if}}

					{{#if this.isApiMetricBuilder}}
						<a class="cancel" href target="_blank">
							Read more about custom API integrations
						</a>
					{{/if}}
				{{/unless}}

				{{#if this.showCancelLink}}
					<SubmitButtonComponent
						data-testid="test-confirmPopupCancelActionButton"
						@text={{this.cancelText}}
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
				{{/if}}

				{{#if this.isRequestTrialButtonShown}}
					<CommonRequestTrialButtonComponent
						@feature={{@feature}}
						@trackingOrigin={{@trackingOrigin}}
						@onSuccess={{this.close}}
						@theme={{@buttonTheme}}
						@size={{@buttonSize}}
					/>
				{{else}}
					{{#if this.isActivityLog}}
						<SubmitButtonComponent
							@text="Enable Advanced security"
							@noDelay={{true}}
							@icon="ArrowUpCircleFill"
							@theme={{@buttonTheme}}
							@size={{@buttonSize}}
							@onTrigger={{this.openActivityLogPurchasePopup}}
						/>
					{{else if this.isOKRs}}
						<SubmitButtonComponent
							@text="Enable OKRs"
							@noDelay={{true}}
							@icon="ArrowUpCircleFill"
							@theme={{@buttonTheme}}
							@size={{@buttonSize}}
							@onTrigger={{this.openOKRsPurchasePopup}}
						/>
					{{else}}
						<CommonUpgradePlanButtonComponent
							@text={{this.upgradeButtonText}}
							@trackingOrigin={{@trackingOrigin}}
							@icon="ArrowUpCircleFill"
							@feature={{@feature}}
							@theme={{@buttonTheme}}
							@size={{@buttonSize}}
						/>
					{{/if}}
				{{/if}}

			{{else}}

				{{#if this.isAgencyAdminInClientSpace}}
					<SubmitButtonComponent
						@text="Switch to organization"
						@theme={{@buttonTheme}}
						@size={{@buttonSize}}
						@onTrigger={{this.redirectToAgencySpace}}
					/>
				{{else}}
					{{#if this.showRequestUpgradeForAgencyNonAdmins}}

						<SubmitButtonComponent
							@text="Request plan upgrade"
							@theme={{@buttonTheme}}
							@size={{@buttonSize}}
							@onTrigger={{this.requestPlanUpgrade}}
						/>

					{{else if this.agencyAdmin}}

						<div class="account-owner-banner">

							<AvatarOrInitialsComponent
								@name={{this.agencyAdmin.name}}
								@avatar={{this.agencyAdmin.avatar}}
							/>

							<div class="info">
								<div class="name">
									<strong>{{this.agencyAdmin.name}}</strong>
									(Account owner)
								</div>
								<div class="email">{{this.agencyAdmin.email}}</div>
							</div>

							<SubmitButtonComponent
								@text="Request plan upgrade"
								@theme={{@buttonTheme}}
								@size={{@buttonSize}}
								@onTrigger={{this.requestPlanUpgrade}}
							/>

						</div>

					{{/if}}
				{{/if}}
			{{/if}}

		</div>
	</template>
}
